import { apiFetch } from '@/utils/api-fetch'
import { computed, ref, unref } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { useMeta } from '../useMeta'
import { useRegionalView } from '../useParams/params/regional-view'

/**
 * Use the list of countries.
 */
export const useCountries = createSharedComposable(() => {
    /**
     * Use the meta data.
     */
    const { meta } = useMeta()

    /**
     * Store the list of countries.
     */
    const countries = ref<Country[]>([])

    /**
     * List of countries' codes.
     */
    const codes = computed(() => {
        return unref(countries).map(country => country.iso)
    })

    /**
     * Region map with countries list.
     */
    const byRegion = computed(() => {
        const data = unref(meta.regions).map(({ id }) => {
            const list = unref(countries)
                .filter(({ region }) => region === id)
                .map(({ iso }) => iso)

            return [id, list] as const
        })

        return new Map(data)
    })

    /**
     * Fetch the list of countries.
     */
    async function fetch() {
        const { countries: data } = await apiFetch<{ countries: Country[] }>(
            'countries'
        )
        countries.value = data
    }

    /**
     * Find a country by its ISO code.
     */
    function find(iso: string) {
        return unref(countries).find(country => country.iso === iso)
    }

    /**
     * Get a country's name by its ISO code.
     */
    function getName(iso: string) {
        return find(iso)?.name || iso
    }

    /**
     * Get a country's income by its ISO code.
     */
    function getIncome(iso: string) {
        return find(iso)?.income
    }

    /**
     * Get a country's region by its ISO code.
     */
    function getRegion(iso: string) {
        const regionId = find(iso)?.region

        if (!regionId) return

        return unref(meta.regions)?.find(({ id }) => id === regionId)
    }

    /**
     * Get the list of countries of the same region.
     */
    function getCountriesInRegion(iso: string) {
        const region = getRegion(iso)

        if (!region) throw Error(`Could not find region for: ${iso}`)

        return unref(byRegion).get(region.id)
    }

    /**
     * Get a country's color by its ISO code.
     */
    function getColor(iso: string) {
        return getRegion(iso)?.color
    }

    /**
     * Check if a country is a G20 member.
     */
    function isG20(iso: string) {
        return find(iso)?.g20 === 1
    }

    /**
     * Get prevalence global ranking.
     */
    function getPrevalenceRanking(
        iso: string,
        regional = unref(useRegionalView())
    ) {
        return find(iso)?.data?.[2023]?.[regional ? 'pr' : 'pg'] ?? 0
    }

    return {
        fetch,
        codes,
        countries,
        getName,
        getColor,
        find,
        getRegion,
        byRegion,
        getIncome,
        isG20,
        getPrevalenceRanking,
        getCountriesInRegion
    }
})
